import { List, Show, Create, Edit, Datagrid, TextField, DateField, ChipField, NumberField, SimpleForm, TextInput, SelectInput, Filter, SearchInput, useGetList, useUpdate, useNotify, useRefresh, useNavigate, ReferenceInput } from 'react-admin';
import { useState } from 'react';
import { Box, Typography, Paper, LinearProgress, Chip, Avatar, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery, TablePagination, Card, CardContent, CircularProgress, Button, IconButton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor, ProgressColor } from '@/types/chip';
import type { EmployeeRecord, TrainingRecord } from '@/resources/hr/types';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';
import { School, TrendingDown, CheckCircle, Cancel, HourglassEmpty, PlayArrow, Assessment, Close, PlaylistAddCheck } from '@mui/icons-material';

interface LearnershipContract {
  id: string;
  status: string;
  contractType: string;
  contractNumber: string;
  courseName: string;
  startDate: string;
  endDate: string;
  seta: string;
  employee?: { id: string };
}

export const WspAtrDashboard = () => {
  const { data: employees } = useGetList('employees', { pagination: { page: 1, perPage: 1000 } });
  const { data: trainings } = useGetList('training-records', { pagination: { page: 1, perPage: 1000 } });

  const totalEmployees = employees?.length ?? 0;

  const raceCounts: Record<string, number> = {};
  employees?.forEach((e: EmployeeRecord) => {
    const race = e.demographics?.race || 'Unknown';
    raceCounts[race] = (raceCounts[race] || 0) + 1;
  });
  const raceData = [
    { label: 'African', value: raceCounts['African'] ?? 0, color: 'primary' },
    { label: 'Coloured', value: raceCounts['Coloured'] ?? 0, color: 'info' },
    { label: 'Indian', value: raceCounts['Indian'] ?? 0, color: 'warning' },
    { label: 'White', value: raceCounts['White'] ?? 0, color: 'secondary' },
  ];

  const genderCounts: Record<string, number> = {};
  employees?.forEach((e: EmployeeRecord) => {
    const gender = e.demographics?.gender || 'Unknown';
    genderCounts[gender] = (genderCounts[gender] || 0) + 1;
  });

  const disabilityCount = employees?.filter((e: EmployeeRecord) => e.demographics?.disabilityStatus?.hasDisability).length ?? 0;

  const trainingCompleted = trainings?.filter((t: TrainingRecord) => t.status === 'Completed').length ?? 0;
  const trainingPlanned = trainings?.filter((t: TrainingRecord) => t.status === 'Planned').length ?? 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>WSP/ATR Report Dashboard — 2026</Typography>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 3, minWidth: 280 }}>
          <Typography variant="body2" color="text.secondary">Reporting Year</Typography>
          <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>2026</Typography>
          <Box sx={{ mt: 1 }}><Typography variant="caption" color="warning.main">Draft</Typography></Box>
        </Paper>
        <Paper sx={{ p: 3, minWidth: 280 }}>
          <Typography variant="body2" color="text.secondary">SETA</Typography>
          <Typography variant="body1" sx={{ mt: 0.5 }}>Services SETA</Typography>
        </Paper>
        <Paper sx={{ p: 3, minWidth: 280 }}>
          <Typography variant="body2" color="text.secondary">Total Employees</Typography>
          <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>{totalEmployees.toLocaleString()}</Typography>
        </Paper>
        <Paper sx={{ p: 3, minWidth: 280 }}>
          <Typography variant="body2" color="text.secondary">Disability Headcount</Typography>
          <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>{disabilityCount}</Typography>
        </Paper>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, mt: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 3, flex: 1, minWidth: 360 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Workforce Profile</Typography>
          {raceData.map(race => (
            <Box key={race.label} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{race.label}</Typography>
                <Typography variant="body2" fontWeight={600}>{race.value}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={totalEmployees > 0 ? (race.value / totalEmployees) * 100 : 0} color={race.color as ProgressColor} sx={{ height: 8, borderRadius: 2 }} />
            </Box>
          ))}
        </Paper>

        <Paper sx={{ p: 3, flex: 1, minWidth: 360 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Training Summary</Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Completed</Typography>
            <Typography variant="h5" fontWeight={700}>{trainingCompleted}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">Planned</Typography>
            <Typography variant="h5" fontWeight={700}>{trainingPlanned}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Records</Typography>
            <Typography variant="h5" fontWeight={700}>{trainings?.length ?? 0}</Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

const CONTRACT_TYPES = ['Skills Programme', 'Learnership', 'Apprenticeship', 'Internship'];
const CONTRACT_STATUS = ['Active', 'Completed', 'Cancelled', 'Expired'];
const STATUS_COLORS: Record<string, string> = {
  Active: 'info',
  Completed: 'success',
  Cancelled: 'error',
  Expired: 'default',
};
const CONTRACT_TYPE_COLORS: Record<string, string> = {
  'Skills Programme': '#1976d2',
  'Learnership': '#2e7d32',
  'Apprenticeship': '#ed6c02',
  'Internship': '#9c27b0',
};

const ContractSparkline = ({ endDate, status }: { endDate: string; status: string }) => {
  const [now] = useState(() => Date.now());

  if (status === 'Completed' || status === 'Cancelled' || status === 'Expired') {
    const icon = status === 'Completed' ? <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} /> :
                 status === 'Cancelled' ? <Cancel sx={{ fontSize: 14, color: 'error.main' }} /> :
                 null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="caption" sx={{ color: status === 'Completed' ? 'success.main' : status === 'Cancelled' ? 'error.main' : 'text.secondary', fontWeight: 600 }}>
          {status}
        </Typography>
      </Box>
    );
  }

  const end = new Date(endDate).getTime();
  const remaining = end - now;
  const daysRemaining = Math.ceil(remaining / (1000 * 60 * 60 * 24));
  const totalDays = 365;
  const pctRemaining = Math.max(0, Math.min(100, (remaining / (totalDays * 24 * 60 * 60 * 1000)) * 100));

  const getColor = () => {
    if (daysRemaining <= 0) return 'error';
    if (daysRemaining <= 30) return 'error';
    if (daysRemaining <= 60) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box sx={{ flex: 1, position: 'relative' }}>
        <Box sx={{ height: 8, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              width: `${pctRemaining}%`,
              height: '100%',
              bgcolor: `${getColor()}.main`,
              borderRadius: 1,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
        <Box sx={{ position: 'absolute', right: 0, top: -2 }}>
          <TrendingDown sx={{ fontSize: 12, color: `${getColor()}.main` }} />
        </Box>
      </Box>
      <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right', color: `${getColor()}.main`, fontWeight: 600 }}>
        {daysRemaining <= 0 ? 'Expired' : `${daysRemaining}d`}
      </Typography>
    </Box>
  );
};

export const ContractList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: contracts, isLoading } = useGetList('contracts', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const employeeMap = useEmployeeMap();

  const stats = {
    total: contracts?.length ?? 0,
    active: contracts?.filter((c: LearnershipContract) => c.status === 'Active').length ?? 0,
    completed: contracts?.filter((c: LearnershipContract) => c.status === 'Completed').length ?? 0,
    cancelled: contracts?.filter((c: LearnershipContract) => c.status === 'Cancelled').length ?? 0,
    expired: contracts?.filter((c: LearnershipContract) => c.status === 'Expired').length ?? 0,
  };

  const typeCounts = contracts?.reduce((acc: Record<string, number>, c: LearnershipContract) => {
    acc[c.contractType] = (acc[c.contractType] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData = Object.entries(typeCounts).map(([label, value]) => ({ label, value: value as number }));

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="hr.skills-development" wizardPath="wizard/wsp-atr" label="WSP/ATR Report" />
        </Box>
        <ListPageHeader
          title="Learnership Contracts"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'info.main' },
            { label: 'Completed', value: String(stats.completed), color: 'success.main' },
            { label: 'Cancelled', value: String(stats.cancelled), color: 'error.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {contracts?.map((contract: LearnershipContract) => (
            <Paper key={contract.id} sx={{ p: 2 }} onClick={() => navigate(`/skills-development/contracts/${contract.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: CONTRACT_TYPE_COLORS[contract.contractType] || 'primary.main', fontSize: 14 }}>
                    <School sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, contract.employee?.id)}</Typography>
                    <Typography variant="caption" color="text.secondary">{contract.contractNumber}</Typography>
                  </Box>
                </Box>
                <Chip label={contract.status} size="small" color={STATUS_COLORS[contract.status] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">{contract.courseName}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{contract.contractType}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">{contract.startDate} → {contract.endDate}</Typography>
                <ContractSparkline endDate={contract.endDate} status={contract.status} />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <WizardListToolbar domainId="hr.skills-development" wizardPath="wizard/wsp-atr" label="WSP/ATR Report" />
      </Box>
      <ListPageHeader
        title="Learnership Contracts"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'info.main' },
          { label: 'Completed', value: String(stats.completed), color: 'success.main' },
          { label: 'Cancelled', value: String(stats.cancelled), color: 'error.main' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="Contract Type"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Learner</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Contract #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Programme</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Time Remaining</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {contracts?.map((contract: LearnershipContract) => (
              <TableRow
                key={contract.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/skills-development/contracts/${contract.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: CONTRACT_TYPE_COLORS[contract.contractType] || 'primary.main', fontSize: 12 }}>
                      {getEmployeeName(employeeMap, contract.employee?.id)?.charAt(0) || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, contract.employee?.id)}</Typography>
                      <Typography variant="caption" color="text.secondary">{contract.seta}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{contract.contractNumber}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{contract.courseName}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={contract.contractType}
                    size="small"
                    sx={{ bgcolor: `${CONTRACT_TYPE_COLORS[contract.contractType]}20`, color: CONTRACT_TYPE_COLORS[contract.contractType], fontWeight: 500 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip label={contract.status} size="small" color={STATUS_COLORS[contract.status] as ChipColor} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{contract.startDate} → {contract.endDate}</Typography>
                </TableCell>
                <TableCell>
                  <ContractSparkline endDate={contract.endDate} status={contract.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={contracts?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!contracts || contracts.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <School sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No learnership contracts found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const ContractFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search contract # or learner" />
    <SelectInput source="status" choices={CONTRACT_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
    <SelectInput source="contractType" choices={CONTRACT_TYPES.map(t => ({ id: t, name: t }))} />
  </Filter>
);

export const ContractShow = () => {
  const navigate = useNavigate();
  const [update, { isLoading: isUpdating }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  return (
    <Show render={({ record }) => {
      if (!record) return null;

      const handleStatusUpdate = (newStatus: string) => {
        update('contracts', { id: record.id, data: { status: newStatus, previousData: record } }, {
          onSuccess: () => {
            notify(`Contract ${newStatus.toLowerCase()}`, { type: 'success' });
            refresh();
          },
          onError: (error: Error) => {
            notify(`Error: ${error.message || 'Update failed'}`, { type: 'warning' });
          },
        });
      };

      const stages = ['Draft', 'Active', 'Completed', 'Cancelled'];
      const currentIndex = stages.indexOf(record.status);
      const stageColors = ['warning', 'info', 'success', 'error'];

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/contracts')} size="small" sx={{ color: 'text.secondary' }}>
              <Close />
            </IconButton>
          </Box>
          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #00695c, #00897b)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{record.contractNumber || 'Contract'}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>{record.employeeName} · {record.courseName}</Typography>
              </Box>
              <Chip label={record.status} size="medium" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, backdropFilter: 'blur(4px)' }} />
            </Box>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
            {stages.map((stage, idx) => {
              const isActive = idx === currentIndex;
              const isPast = idx < currentIndex;
              const isClickable = idx === currentIndex + 1;
              return (
                <Card
                  key={stage}
                  onClick={() => isClickable && handleStatusUpdate(stage)}
                  sx={{
                    cursor: isClickable ? 'pointer' : 'default',
                    border: isActive ? 2 : 1,
                    borderColor: isActive ? `${stageColors[idx]}.main` : 'divider',
                    bgcolor: isActive ? `${stageColors[idx]}.light` : isPast ? 'action.hover' : 'background.paper',
                    transition: 'all 0.2s',
                    '&:hover': isClickable ? { transform: 'translateY(-2px)', boxShadow: 3 } : {},
                    opacity: idx < currentIndex ? 0.7 : 1,
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h5" sx={{ color: `${stageColors[idx]}.main` }}>{idx + 1}</Typography>
                    <Typography variant="body2" fontWeight={isActive ? 700 : 500} sx={{ color: isActive ? `${stageColors[idx]}.dark` : 'text.primary' }}>
                      {stage}
                    </Typography>
                    {isActive && <HourglassEmpty sx={{ fontSize: 16, color: `${stageColors[idx]}.main`, mt: 0.5 }} />}
                    {isClickable && <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>Click to advance</Typography>}
                  </CardContent>
                </Card>
              );
            })}
          </Box>

          {((record.status === 'Draft') || (record.status === 'Active') || (record.status === 'Completed')) && (
            <Paper variant="outlined" sx={{ p: 3, my: 3, bgcolor: 'action.hover', borderTop: 4, borderTopColor: 'primary.main', borderLeft: 0, borderRight: 0, borderBottom: 0, borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PlaylistAddCheck fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Process Actions</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {isUpdating && <CircularProgress size={18} sx={{ color: 'text.disabled' }} />}
                {record.status === 'Draft' && (
                  <Button variant="contained" startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />} onClick={() => handleStatusUpdate('Active')} disabled={isUpdating}>
                    Activate Contract
                  </Button>
                )}
                {record.status === 'Active' && (
                  <>
                    <Button variant="contained" color="success" startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />} onClick={() => handleStatusUpdate('Completed')} disabled={isUpdating}>
                      Complete
                    </Button>
                    <Button variant="outlined" color="error" startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <Cancel />} onClick={() => handleStatusUpdate('Cancelled')} disabled={isUpdating}>
                      Cancel
                    </Button>
                  </>
                )}
                {record.status === 'Completed' && (
                  <Button variant="contained" startIcon={isUpdating ? <CircularProgress size={14} color="inherit" /> : <Assessment />} onClick={() => handleStatusUpdate('Reported')} disabled={isUpdating}>
                    Mark as Reported (SETS)
                  </Button>
                )}
              </Box>
            </Paper>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
            {[
              { label: 'Contract Number', value: record.contractNumber },
              { label: 'Learner', value: record.employeeName },
              { label: 'Programme', value: record.courseName },
              { label: 'Contract Type', value: record.contractType },
              { label: 'Status', value: record.status },
              { label: 'Start Date', value: record.startDate },
              { label: 'End Date', value: record.endDate },
              { label: 'SETA', value: record.seta },
            ].map(f => (
              <Box key={f.label}>
                <Typography variant="caption" color="text.secondary">{f.label}</Typography>
                <Typography variant="body1" fontWeight={f.label === 'Contract Number' ? 700 : 400}>{f.value ?? '—'}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      );
    }} />
  );
};

export const ContractCreate = () => (
  <Create>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="contractNumber" label="Contract Number" fullWidth />
        <ReferenceInput source="employee.id" reference="employees" fullWidth>
          <SelectInput optionText="name" label="Learner" />
        </ReferenceInput>
        <TextInput source="courseName" label="Programme Name" fullWidth />
        <SelectInput source="contractType" choices={CONTRACT_TYPES.map(t => ({ id: t, name: t }))} fullWidth />
        <DateField source="startDate" />
        <DateField source="endDate" />
        <TextInput source="seta" label="SETA" fullWidth />
        <SelectInput source="status" choices={CONTRACT_STATUS.map(s => ({ id: s, name: s }))} fullWidth />
      </Box>
    </SimpleForm>
  </Create>
);

export const ContractEdit = () => (
  <Edit>
    <SimpleForm>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <TextInput source="contractNumber" label="Contract Number" fullWidth />
        <ReferenceInput source="employee.id" reference="employees" fullWidth>
          <SelectInput optionText="name" label="Learner" />
        </ReferenceInput>
        <TextInput source="courseName" label="Programme Name" fullWidth />
        <SelectInput source="contractType" choices={CONTRACT_TYPES.map(t => ({ id: t, name: t }))} fullWidth />
        <DateField source="startDate" />
        <DateField source="endDate" />
        <TextInput source="seta" label="SETA" fullWidth />
        <SelectInput source="status" choices={CONTRACT_STATUS.map(s => ({ id: s, name: s }))} fullWidth />
      </Box>
    </SimpleForm>
  </Edit>
);